/**
 * Criar, editar e acompanhar uma publicação.
 *
 * Um diálogo só para os três momentos, porque a pessoa chega aqui pelo mesmo
 * clique no calendário: post que ainda não saiu abre para edição; post que já
 * começou a sair abre o andamento de cada conta (publicado, falhou e por quê,
 * tentar de novo).
 *
 * As regras de cada rede (`validarPost`) rodam enquanto a pessoa monta o post —
 * descobrir na hora marcada que o vídeo tem um segundo a mais que o permitido
 * é descobrir quando ninguém está olhando.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import type { inferRouterOutputs } from "@trpc/server";
import type { AppRouter } from "../../../../server/routers";
import { trpc } from "@/lib/trpc";
import { toUserFriendlyError } from "@/lib/error-messages";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  ExternalLink,
  FlaskConical,
  ImagePlus,
  Link2,
  Loader2,
  RotateCcw,
  Trash2,
  X,
} from "lucide-react";
import { toast } from "sonner";
import {
  FORMAT_LABELS,
  FORMATOS_POR_REDE,
  IG_CAPTION_MAX,
  IG_MAX_HASHTAGS,
  LINK_CLICAVEL_NA_LEGENDA,
  LINK_PLACEHOLDER,
  MAX_MEDIA_ITEMS,
  NETWORK_LABELS,
  POST_STATUS_LABELS,
  TIKTOK_OPTIONS_PADRAO,
  TRIAL_GRADUATION_LABELS,
  YOUTUBE_OPTIONS_PADRAO,
  contarHashtags,
  validarPost,
  type SocialFormat,
  type SocialNetwork,
  type TikTokOptions,
  type TrialGraduation,
  type PostStatus,
  type YouTubeOptions,
} from "@shared/social-publishing";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { IconeRede } from "./redes";
import { OpcoesTikTok, OpcoesYouTube } from "./OpcoesDasRedes";
import { paraInputLocal, doInputLocal } from "@/components/Agenda/agenda-utils";
import { prepararArquivo, enviarParaBucket, type MidiaPronta } from "./upload";

export type PostDaTela = inferRouterOutputs<AppRouter>["social"]["posts"]["list"][number];
type Destinos = inferRouterOutputs<AppRouter>["social"]["status"]["destinos"];

const STATUS_ALVO: Record<string, { rotulo: string; cor: string }> = {
  scheduled: { rotulo: "Agendado", cor: "bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200" },
  publishing: { rotulo: "Publicando", cor: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200" },
  published: { rotulo: "Publicado", cor: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200" },
  failed: { rotulo: "Falhou", cor: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200" },
  canceled: { rotulo: "Cancelado", cor: "bg-muted text-muted-foreground" },
};

type Alvo = { network: SocialNetwork; accountId: number };
const chave = (a: Alvo) => `${a.network}:${a.accountId}`;
const ORDEM_DAS_REDES: SocialNetwork[] = ["instagram", "facebook", "tiktok", "youtube", "linkedin"];

/** Todas as contas, na ordem das redes. */
function contasDe(destinos: Destinos | undefined) {
  if (!destinos) return [];
  return ORDEM_DAS_REDES.flatMap((rede) => (destinos[rede] ?? []).map((c) => ({ ...c, network: rede })));
}

function midiaDoPost(p: PostDaTela): MidiaPronta[] {
  return p.media.map((m, i) => ({
    key: `${p.id}-${i}`,
    kind: m.kind as MidiaPronta["kind"],
    mimeType: m.mimeType,
    sizeBytes: m.sizeBytes,
    width: m.width,
    height: m.height,
    durationMs: m.durationMs,
    previewUrl: m.previewUrl ?? "",
    path: m.path,
    progresso: 100,
    erro: null,
    arquivo: null,
  }));
}

export default function PostComposer({
  aberto,
  onFechar,
  post,
  diaInicial,
  destinos,
  armazenamentoPronto,
  tiktokFotos = false,
  tamanhoDoLink,
}: {
  aberto: boolean;
  onFechar: () => void;
  /** Post existente (editar ou acompanhar). Null = novo. */
  post: PostDaTela | null;
  /** Dia clicado no calendário, para já vir preenchido. */
  diaInicial?: Date | null;
  destinos: Destinos | undefined;
  armazenamentoPronto: boolean;
  /** Foto no TikTok liberada no servidor (domínio verificado). */
  tiktokFotos?: boolean;
  /** Tamanho real do link curto neste servidor. */
  tamanhoDoLink?: number;
}) {
  const utils = trpc.useUtils();
  const editavel =
    !post || post.status === "draft" || (post.status === "scheduled" && post.targets.every((t) => t.status === "scheduled"));

  const [formato, setFormato] = useState<SocialFormat>("feed");
  const [legenda, setLegenda] = useState("");
  const [alvos, setAlvos] = useState<Alvo[]>([]);
  const [midias, setMidias] = useState<MidiaPronta[]>([]);
  const [quando, setQuando] = useState<"agendar" | "agora">("agendar");
  const [dataHora, setDataHora] = useState("");
  const [teste, setTeste] = useState(false);
  const [graduacao, setGraduacao] = useState<TrialGraduation>("MANUAL");
  const [noFeed, setNoFeed] = useState(true);
  const [primeiroComentario, setPrimeiroComentario] = useState("");
  const [tiktok, setTiktok] = useState<TikTokOptions>(TIKTOK_OPTIONS_PADRAO);
  const [youtube, setYoutube] = useState<YouTubeOptions>(YOUTUBE_OPTIONS_PADRAO);
  const [linkId, setLinkId] = useState<number | null>(null);
  const inputArquivo = useRef<HTMLInputElement>(null);
  const legendaRef = useRef<HTMLTextAreaElement>(null);

  // Reabre limpo (ou com o post) a cada abertura — e SÓ nela, ou quando muda
  // o post. O objeto `post` é renovado a cada atualização da lista; depender
  // dele apagaria o que a pessoa está digitando a cada 20 s.
  useEffect(() => {
    if (!aberto) return;
    if (post) {
      setFormato(post.format as SocialFormat);
      setLegenda(post.caption);
      // Conta desconectada não aparece na lista para desmarcar: tirá-la já
      // aqui evita o "atualize a página" que nunca resolveria.
      // Só com a lista de contas carregada: sem ela, todas pareceriam
      // desconectadas e salvar apagaria as contas do post.
      const conectadas = destinos ? new Set(contasDe(destinos).map((c) => `${c.network}:${c.id}`)) : null;
      setAlvos(
        post.targets
          .filter((t) => t.status !== "canceled" && t.accountId != null)
          .map((t) => ({ network: t.network as SocialNetwork, accountId: t.accountId as number }))
          .filter((a) => !conectadas || conectadas.has(chave(a))),
      );
      setMidias(midiaDoPost(post));
      setQuando("agendar");
      setDataHora(post.scheduledAt ? paraInputLocal(post.scheduledAt) : "");
      setTeste(!!post.trialGraduation);
      setGraduacao((post.trialGraduation as TrialGraduation) ?? "MANUAL");
      setNoFeed(post.shareToFeed);
      setPrimeiroComentario(post.firstComment ?? "");
      setTiktok({ ...TIKTOK_OPTIONS_PADRAO, ...(post.networkOptions?.tiktok ?? {}) });
      setYoutube({ ...YOUTUBE_OPTIONS_PADRAO, ...(post.networkOptions?.youtube ?? {}) });
      setLinkId(post.leadLinkId ?? null);
    } else {
      setFormato("feed");
      setLegenda("");
      // Uma conta só no workspace: já vem marcada.
      const todas: Alvo[] = contasDe(destinos).map((c) => ({ network: c.network, accountId: c.id }));
      setAlvos(todas.length === 1 ? todas : []);
      setMidias([]);
      setQuando("agendar");
      const base = diaInicial ? new Date(diaInicial) : new Date(Date.now() + 60 * 60_000);
      if (diaInicial) base.setHours(10, 0, 0, 0);
      setDataHora(paraInputLocal(base));
      setTeste(false);
      setGraduacao("MANUAL");
      setNoFeed(true);
      setPrimeiroComentario("");
      setTiktok(TIKTOK_OPTIONS_PADRAO);
      setYoutube(YOUTUBE_OPTIONS_PADRAO);
      setLinkId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aberto, post?.id, diaInicial, destinos == null]);

  const redes = useMemo(() => [...new Set(alvos.map((a) => a.network))], [alvos]);
  const temIg = redes.includes("instagram");
  const temTiktok = redes.includes("tiktok");
  const temYoutube = redes.includes("youtube");
  const primeiraTiktok = alvos.find((a) => a.network === "tiktok")?.accountId ?? null;
  const enviando = midias.some((m) => !m.path && !m.erro);
  // Formatos que TODAS as redes escolhidas publicam.
  const formatosPossiveis = (["feed", "reel", "story"] as const).filter((f) => redes.every((r) => FORMATOS_POR_REDE[r].includes(f)));
  const maxMidias = redes.some((r) => r === "instagram" || r === "facebook") ? 10 : redes.includes("linkedin") ? 20 : MAX_MEDIA_ITEMS;
  const linksDeCaptacao = trpc.social.captacao.links.useQuery(undefined, { enabled: aberto, staleTime: 60_000 });
  const linksAtivos = (linksDeCaptacao.data?.links ?? []).filter((l) => l.isActive || l.id === linkId);

  const erros = useMemo(
    () =>
      validarPost({
        format: formato,
        // Os mesmos valores que vão no payload: story sai sem legenda e sem link.
        caption: formato === "story" ? "" : legenda,
        media: midias.filter((m) => !m.erro).map((m) => ({
          kind: m.kind,
          mimeType: m.mimeType,
          sizeBytes: m.sizeBytes,
          durationMs: m.durationMs,
          width: m.width,
          height: m.height,
        })),
        networks: redes,
        trialGraduation: formato === "reel" && teste ? graduacao : null,
        scheduledAt: quando === "agendar" && dataHora ? doInputLocal(dataHora) : null,
        networkOptions: { tiktok, youtube },
        temLinkDeCaptacao: formato !== "story" && linkId != null,
        firstComment: formato === "story" ? null : primeiroComentario,
        capacidades: { tiktokFotos },
        tamanhoDoLink,
      }),
    [formato, legenda, midias, redes, teste, graduacao, quando, dataHora, tiktok, youtube, linkId, tiktokFotos, tamanhoDoLink, primeiroComentario],
  );

  const createUpload = trpc.social.media.createUpload.useMutation();
  const invalidar = () => void utils.social.posts.list.invalidate();
  const aoSalvar = (msg: string) => {
    invalidar();
    toast.success(msg);
    onFechar();
  };
  const criar = trpc.social.posts.create.useMutation({ onError: (e) => toast.error(toUserFriendlyError(e)) });
  const atualizar = trpc.social.posts.update.useMutation({ onError: (e) => toast.error(toUserFriendlyError(e)) });
  const cancelar = trpc.social.posts.cancel.useMutation({
    onSuccess: () => aoSalvar("Agendamento cancelado."),
    onError: (e) => toast.error(toUserFriendlyError(e)),
  });
  const remover = trpc.social.posts.remove.useMutation({
    onSuccess: () => aoSalvar("Publicação apagada."),
    onError: (e) => toast.error(toUserFriendlyError(e)),
  });
  const repetir = trpc.social.posts.retryTarget.useMutation({
    onSuccess: () => {
      invalidar();
      void utils.social.posts.get.invalidate();
      toast.success("Vou tentar publicar de novo agora.");
    },
    onError: (e) => toast.error(toUserFriendlyError(e)),
  });
  const salvando = criar.isPending || atualizar.isPending;

  const atualizarMidia = (key: string, patch: Partial<MidiaPronta>) =>
    setMidias((lista) => lista.map((m) => (m.key === key ? { ...m, ...patch } : m)));

  async function adicionarArquivos(arquivos: FileList | null) {
    if (!arquivos?.length) return;
    const lista = Array.from(arquivos);
    const umSo = formato !== "feed";
    const escolhidos = umSo ? lista.slice(0, 1) : lista;
    if (umSo) setMidias([]);
    for (const arquivo of escolhidos) {
      let m: MidiaPronta;
      try {
        m = await prepararArquivo(arquivo);
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "Arquivo inválido.");
        continue;
      }
      setMidias((atual) => [...atual, m]);
      try {
        const { path, uploadUrl } = await createUpload.mutateAsync({ mimeType: m.mimeType, sizeBytes: m.sizeBytes });
        await enviarParaBucket(uploadUrl, m.arquivo!, m.mimeType, (pct) => atualizarMidia(m.key, { progresso: pct }));
        atualizarMidia(m.key, { path, progresso: 100, arquivo: null });
      } catch (err) {
        atualizarMidia(m.key, { erro: err instanceof Error ? err.message : toUserFriendlyError(err) });
      }
    }
  }

  const mover = (i: number, d: -1 | 1) =>
    setMidias((lista) => {
      const j = i + d;
      if (j < 0 || j >= lista.length) return lista;
      const nova = [...lista];
      [nova[i], nova[j]] = [nova[j], nova[i]];
      return nova;
    });

  function payload(rascunho: boolean) {
    return {
      format: formato,
      caption: formato === "story" ? "" : legenda,
      draft: rascunho,
      publicarAgora: !rascunho && quando === "agora",
      scheduledAt: quando === "agendar" && dataHora ? doInputLocal(dataHora) : null,
      trialGraduation: formato === "reel" && teste ? graduacao : null,
      shareToFeed: noFeed,
      firstComment: formato === "story" ? null : primeiroComentario.trim() || null,
      networkOptions: {
        ...(temTiktok ? { tiktok } : {}),
        ...(temYoutube ? { youtube } : {}),
      },
      leadLinkId: formato === "story" ? null : linkId,
      media: midias
        .filter((m) => m.path)
        .map((m) => ({ path: m.path!, mimeType: m.mimeType, width: m.width, height: m.height, durationMs: m.durationMs })),
      targets: alvos,
    };
  }

  async function salvar(rascunho: boolean) {
    const data = payload(rascunho);
    try {
      if (post) await atualizar.mutateAsync({ id: post.id, data });
      else await criar.mutateAsync(data);
      aoSalvar(rascunho ? "Rascunho salvo." : quando === "agora" ? "Publicando agora…" : "Publicação agendada.");
    } catch {
      // A mensagem já foi para o toast pelo onError.
    }
  }

  const alternarAlvo = (a: Alvo) =>
    setAlvos((lista) => (lista.some((x) => chave(x) === chave(a)) ? lista.filter((x) => chave(x) !== chave(a)) : [...lista, a]));

  const hashtags = contarHashtags(legenda);
  const contas = contasDe(destinos);
  const semContas = contas.length === 0;

  /** Põe `{link}` onde está o cursor da legenda. */
  function inserirLink() {
    const el = legendaRef.current;
    const pos = el?.selectionStart ?? legenda.length;
    const antes = legenda.slice(0, pos);
    const espaco = antes && !/\s$/.test(antes) ? " " : "";
    setLegenda(`${antes}${espaco}${LINK_PLACEHOLDER}${legenda.slice(pos)}`);
  }

  return (
    <Dialog open={aberto} onOpenChange={(v) => !v && onFechar()}>
      <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {!post ? "Nova publicação" : editavel ? "Editar publicação" : "Publicação"}
            {post && (
              <Badge variant="outline" className="ml-2 align-middle">
                {POST_STATUS_LABELS[post.status as PostStatus] ?? post.status}
              </Badge>
            )}
          </DialogTitle>
          <DialogDescription>
            {editavel
              ? "Monte o post, escolha as contas e quando ele sai. O LeadConversa publica na hora marcada."
              : "Este post já começou a sair. Acompanhe cada conta abaixo."}
          </DialogDescription>
        </DialogHeader>

        {/* ── Andamento por conta (post que já começou) ─────────────────── */}
        {post && !editavel && (
          <div className="space-y-2">
            {post.targets.map((t) => {
              const st = STATUS_ALVO[t.status] ?? STATUS_ALVO.scheduled;
              return (
                <div key={t.id} className="rounded border p-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <IconeRede rede={t.network} />
                    <span className="font-medium">{t.nome}</span>
                    <Badge className={st.cor}>{st.rotulo}</Badge>
                    {t.link && t.link.cliques != null && t.link.leads != null && (
                      <span className="text-xs text-muted-foreground" title={t.link.url}>
                        {t.link.cliques} {t.link.cliques === 1 ? "clique" : "cliques"} · {t.link.leads}{" "}
                        {t.link.leads === 1 ? "lead" : "leads"}
                      </span>
                    )}
                    {t.permalink && (
                      <a
                        href={t.permalink}
                        target="_blank"
                        rel="noreferrer"
                        className="ml-auto inline-flex items-center gap-1 text-sm text-primary hover:underline"
                      >
                        Ver post <ExternalLink className="h-3.5 w-3.5" />
                      </a>
                    )}
                  </div>
                  {t.lastError && (
                    <p
                      className={`mt-2 text-sm ${t.status === "published" ? "text-muted-foreground" : "text-red-700 dark:text-red-300"}`}
                    >
                      {t.lastError}
                    </p>
                  )}
                  {t.status === "scheduled" && t.nextAttemptAt && (
                    <p className="mt-1 text-xs text-muted-foreground">
                      Próxima tentativa: {new Date(t.nextAttemptAt).toLocaleString("pt-BR")}
                    </p>
                  )}
                  {t.status === "failed" && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="mt-2"
                      disabled={repetir.isPending}
                      onClick={() => repetir.mutate({ targetId: t.id })}
                    >
                      <RotateCcw className="mr-2 h-4 w-4" /> Tentar de novo
                    </Button>
                  )}
                </div>
              );
            })}
            {post.caption && <p className="whitespace-pre-wrap rounded bg-muted/40 p-3 text-sm">{post.caption}</p>}
          </div>
        )}

        {/* ── Montar o post ──────────────────────────────────────────────── */}
        {editavel && (
          <div className="space-y-5">
            {!armazenamentoPronto && (
              <p className="rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
                O armazenamento de mídia ainda não foi configurado neste servidor — dá para montar posts só de
                texto no Facebook, mas não subir imagens ou vídeos.
              </p>
            )}

            <section className="space-y-2">
              <Label>Onde publicar</Label>
              {semContas ? (
                <p className="text-sm text-muted-foreground">
                  Nenhuma conta conectada. Conecte o Instagram ou uma Página do Facebook em Configurações → Integrações, e o
                  TikTok, o YouTube ou o LinkedIn em Redes sociais → Contas.
                </p>
              ) : (
                <div className="grid gap-2 sm:grid-cols-2">
                  {contas.map((c) => {
                    const a = { network: c.network, accountId: c.id };
                    const marcado = alvos.some((x) => chave(x) === chave(a));
                    return (
                      <label
                        key={chave(a)}
                        className={`flex cursor-pointer items-center gap-2 rounded border p-2 text-sm ${marcado ? "border-primary bg-muted/40" : ""}`}
                      >
                        <Checkbox checked={marcado} onCheckedChange={() => alternarAlvo(a)} />
                        <IconeRede rede={c.network} />
                        {c.foto && <img src={c.foto} alt="" className="h-5 w-5 rounded-full object-cover" />}
                        <span className="truncate">{c.nome}</span>
                        {c.semPermissaoDePublicar && (
                          <span
                            title={
                              c.network === "instagram" || c.network === "facebook"
                                ? "A conta foi conectada sem a permissão de publicar. Reconecte marcando 'publicar'."
                                : "A conexão desta conta venceu ou foi revogada. Reconecte em Redes sociais → Contas."
                            }
                          >
                            <AlertTriangle className="ml-auto h-4 w-4 text-amber-600" />
                          </span>
                        )}
                      </label>
                    );
                  })}
                </div>
              )}
            </section>

            <section className="space-y-2">
              <Label>Formato</Label>
              <div className="flex gap-2">
                {(["feed", "reel", "story"] as const).map((f) => (
                  <Button
                    key={f}
                    type="button"
                    size="sm"
                    variant={formato === f ? "default" : "outline"}
                    disabled={!formatosPossiveis.includes(f) && formato !== f}
                    title={
                      formatosPossiveis.includes(f)
                        ? undefined
                        : `${redes.filter((r) => !FORMATOS_POR_REDE[r].includes(f)).map((r) => NETWORK_LABELS[r]).join(", ")} não publica ${FORMAT_LABELS[f].toLowerCase()}`
                    }
                    onClick={() => {
                      setFormato(f);
                      if (f !== "feed") setMidias((m) => m.slice(0, 1));
                      if (f !== "reel") setTeste(false);
                    }}
                  >
                    {FORMAT_LABELS[f]}
                  </Button>
                ))}
              </div>
              {formato === "feed" && (
                <p className="text-xs text-muted-foreground">
                  Mais de uma mídia vira carrossel (até {maxMidias} com as contas escolhidas).
                  {temYoutube && " No YouTube, o post é um vídeo só."}
                </p>
              )}
              {formato === "reel" && (temYoutube || temTiktok || redes.includes("linkedin")) && (
                <p className="text-xs text-muted-foreground">
                  Vídeo curto vertical{temYoutube ? " — no YouTube vira Short (até 3 min)" : ""}.
                </p>
              )}
              {formato === "story" && (
                <p className="text-xs text-muted-foreground">
                  Pela API da Meta o story sai sem link, enquete, música ou figurinhas — só a imagem ou o vídeo.
                </p>
              )}
            </section>

            <section className="space-y-2">
              <Label>{formato === "reel" ? "Vídeo" : formato === "story" ? "Imagem ou vídeo" : "Mídias"}</Label>
              <div className="flex flex-wrap gap-2">
                {midias.map((m, i) => (
                  <div key={m.key} className="relative h-24 w-24 overflow-hidden rounded border bg-muted">
                    {m.kind === "video" ? (
                      <video src={m.previewUrl || undefined} className="h-full w-full object-cover" muted />
                    ) : m.previewUrl ? (
                      <img src={m.previewUrl} alt="" className="h-full w-full object-cover" />
                    ) : null}
                    {!m.path && !m.erro && (
                      <div className="absolute inset-x-1 bottom-1">
                        <Progress value={m.progresso} className="h-1.5" />
                      </div>
                    )}
                    {m.erro && (
                      <div className="absolute inset-0 flex items-center bg-red-600/80 p-1 text-[10px] leading-tight text-white" title={m.erro}>
                        {m.erro}
                      </div>
                    )}
                    {m.kind === "video" && m.durationMs != null && (
                      <span className="absolute left-1 top-1 rounded bg-black/60 px-1 text-[10px] text-white">
                        {Math.round(m.durationMs / 1000)}s
                      </span>
                    )}
                    <button
                      type="button"
                      className="absolute right-1 top-1 rounded-full bg-black/60 p-0.5 text-white"
                      onClick={() => setMidias((l) => l.filter((x) => x.key !== m.key))}
                      aria-label="Remover"
                    >
                      <X className="h-3 w-3" />
                    </button>
                    {formato === "feed" && midias.length > 1 && (
                      <div className="absolute bottom-1 right-1 flex gap-0.5">
                        <button type="button" className="rounded bg-black/60 p-0.5 text-white" onClick={() => mover(i, -1)} aria-label="Mover para a esquerda">
                          <ArrowLeft className="h-3 w-3" />
                        </button>
                        <button type="button" className="rounded bg-black/60 p-0.5 text-white" onClick={() => mover(i, 1)} aria-label="Mover para a direita">
                          <ArrowRight className="h-3 w-3" />
                        </button>
                      </div>
                    )}
                  </div>
                ))}
                {(formato === "feed" ? midias.length < maxMidias : midias.length === 0) && (
                  <button
                    type="button"
                    disabled={!armazenamentoPronto}
                    onClick={() => inputArquivo.current?.click()}
                    className="flex h-24 w-24 flex-col items-center justify-center gap-1 rounded border border-dashed text-xs text-muted-foreground hover:bg-muted/40 disabled:opacity-50"
                  >
                    <ImagePlus className="h-5 w-5" />
                    Adicionar
                  </button>
                )}
              </div>
              <input
                ref={inputArquivo}
                type="file"
                className="hidden"
                multiple={formato === "feed"}
                accept={formato === "reel" ? "video/mp4,video/quicktime" : "image/*,video/mp4,video/quicktime"}
                onChange={(e) => {
                  void adicionarArquivos(e.target.files);
                  e.target.value = "";
                }}
              />
            </section>

            {formato !== "story" && (
              <section className="space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <Label htmlFor="legenda">{temYoutube && redes.length === 1 ? "Descrição" : "Legenda"}</Label>
                  {linkId != null && !legenda.includes(LINK_PLACEHOLDER) && !primeiroComentario.includes(LINK_PLACEHOLDER) && (
                    <Button type="button" size="sm" variant="ghost" className="h-7" onClick={inserirLink}>
                      <Link2 className="mr-1 h-3.5 w-3.5" /> Inserir {LINK_PLACEHOLDER}
                    </Button>
                  )}
                </div>
                <Textarea id="legenda" ref={legendaRef} rows={5} value={legenda} onChange={(e) => setLegenda(e.target.value)} />
                {temIg && (
                  <p className={`text-xs ${legenda.length > IG_CAPTION_MAX || hashtags > IG_MAX_HASHTAGS ? "text-red-600" : "text-muted-foreground"}`}>
                    {legenda.length.toLocaleString("pt-BR")}/{IG_CAPTION_MAX.toLocaleString("pt-BR")} caracteres · {hashtags}/{IG_MAX_HASHTAGS} hashtags
                  </p>
                )}
              </section>
            )}

            {formato !== "story" && (
              <section className="space-y-2">
                <Label>Link de captação (opcional)</Label>
                <div className="flex flex-wrap items-center gap-2">
                  <Select value={linkId == null ? "nenhum" : String(linkId)} onValueChange={(v) => setLinkId(v === "nenhum" ? null : Number(v))}>
                    <SelectTrigger className="w-full sm:w-80">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="nenhum">Sem link de captação</SelectItem>
                      {linksAtivos.map((l) => (
                        <SelectItem key={l.id} value={String(l.id)}>
                          {l.name} {l.destination === "whatsapp" ? "(WhatsApp)" : l.destination === "pagina" ? "(página de captação)" : "(site)"}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <a href="/social/captacao" target="_blank" rel="noreferrer" className="text-xs text-primary hover:underline">
                    Gerenciar links
                  </a>
                </div>
                <p className="text-xs text-muted-foreground">
                  Cada conta recebe o seu link curto no lugar de {LINK_PLACEHOLDER} — é assim que o lead que chega pelo WhatsApp
                  aparece com a rede de onde veio.
                  {linkId != null &&
                    redes.some((r) => !LINK_CLICAVEL_NA_LEGENDA[r]) &&
                    ` No ${redes
                      .filter((r) => !LINK_CLICAVEL_NA_LEGENDA[r])
                      .map((r) => NETWORK_LABELS[r])
                      .join(" e no ")} o link da legenda não é clicável: use também o link de bio (em Redes sociais → Leads das redes).`}
                </p>
              </section>
            )}

            {temTiktok && primeiraTiktok != null && formato !== "story" && (
              <OpcoesTikTok
                contaId={primeiraTiktok}
                valor={tiktok}
                onChange={setTiktok}
                varias={alvos.filter((a) => a.network === "tiktok").length > 1}
              />
            )}
            {temYoutube && formato !== "story" && <OpcoesYouTube valor={youtube} onChange={setYoutube} />}

            {formato === "reel" && temIg && (
              <section className="space-y-3 rounded border p-3">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <Label className="inline-flex items-center gap-1.5">
                      <FlaskConical className="h-4 w-4" /> Reel de teste
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Mostra o reel primeiro só para quem NÃO segue a conta. Bom para testar um gancho sem expor os
                      seguidores. Só Instagram, e a conta precisa ter o recurso liberado pela Meta.
                    </p>
                  </div>
                  <Switch checked={teste} onCheckedChange={setTeste} />
                </div>
                {teste && (
                  <RadioGroup value={graduacao} onValueChange={(v) => setGraduacao(v as TrialGraduation)}>
                    {(Object.keys(TRIAL_GRADUATION_LABELS) as TrialGraduation[]).map((g) => (
                      <label key={g} className="flex items-center gap-2 text-sm">
                        <RadioGroupItem value={g} /> {TRIAL_GRADUATION_LABELS[g]}
                      </label>
                    ))}
                  </RadioGroup>
                )}
                {!teste && (
                  <label className="flex items-center justify-between gap-4 text-sm">
                    Mostrar também no feed do perfil
                    <Switch checked={noFeed} onCheckedChange={setNoFeed} />
                  </label>
                )}
              </section>
            )}

            {formato !== "story" && temIg && (
              <section className="space-y-2">
                <Label htmlFor="primeiro-comentario">Primeiro comentário (opcional)</Label>
                <Input
                  id="primeiro-comentario"
                  value={primeiroComentario}
                  onChange={(e) => setPrimeiroComentario(e.target.value)}
                  placeholder="Ex.: as hashtags, fora da legenda"
                />
              </section>
            )}

            <section className="space-y-2">
              <Label>Quando</Label>
              <div className="flex flex-wrap items-center gap-2">
                <Button type="button" size="sm" variant={quando === "agendar" ? "default" : "outline"} onClick={() => setQuando("agendar")}>
                  Agendar
                </Button>
                <Button type="button" size="sm" variant={quando === "agora" ? "default" : "outline"} onClick={() => setQuando("agora")}>
                  Publicar agora
                </Button>
                {quando === "agendar" && (
                  <Input
                    type="datetime-local"
                    className="w-auto"
                    value={dataHora}
                    onChange={(e) => setDataHora(e.target.value)}
                  />
                )}
              </div>
            </section>

            {erros.length > 0 && (alvos.length > 0 || midias.length > 0) && (
              <ul className="list-disc space-y-1 rounded border border-amber-300 bg-amber-50 py-2 pl-6 pr-3 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
                {erros.map((e) => (
                  <li key={e}>{e}</li>
                ))}
              </ul>
            )}
          </div>
        )}

        <DialogFooter className="gap-2 sm:justify-between">
          <div className="flex gap-2">
            {post && post.targets.some((t) => t.status === "scheduled" || t.status === "failed") && post.status !== "draft" && (
              <Button variant="outline" onClick={() => cancelar.mutate({ id: post.id })} disabled={cancelar.isPending}>
                {post.targets.some((t) => t.status === "scheduled") ? "Cancelar agendamento" : "Desistir das que falharam"}
              </Button>
            )}
            {post && !post.targets.some((t) => t.status === "published" || t.status === "publishing") && (
              <Button
                variant="ghost"
                className="text-red-600"
                onClick={() => remover.mutate({ id: post.id })}
                disabled={remover.isPending || !!post.bloqueioDeApagar}
                title={post.bloqueioDeApagar ?? "Apaga do LeadConversa. Nada muda nas redes."}
              >
                <Trash2 className="mr-2 h-4 w-4" /> Apagar
              </Button>
            )}
          </div>
          {editavel && (
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => void salvar(true)} disabled={salvando || enviando}>
                Salvar rascunho
              </Button>
              <Button onClick={() => void salvar(false)} disabled={salvando || enviando || erros.length > 0}>
                {(salvando || enviando) && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {enviando ? "Enviando arquivos…" : quando === "agora" ? "Publicar agora" : "Agendar"}
              </Button>
            </div>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
